import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useChat } from '../contexts/ChatContext';
import Avatar from './Avatar';
import {
  IoCall, IoCallSharp, IoMic, IoMicOff,
} from 'react-icons/io5';
import './CallKit.css';

/**
 * CallKit — Audio call UI overlay.
 *
 * Renders different views based on callState:
 * - idle:        nothing rendered
 * - incoming:    incoming call accept/reject UI
 * - calling:     "calling..." UI (for initiator)
 * - connecting:  "connecting..." UI
 * - active:      full call UI with participant tiles
 * - ended:       brief "call ended" then unmount
 */
const CallKit = () => {
  const { t } = useTranslation();
  const {
    callState,
    activeCall,
    incomingCall,
    isMuted,
    remoteStreams,
    remoteMuteStates,
    participantStates,
    speakingPeers,
    acceptCall,
    rejectCall,
    endCurrentCall,
    toggleMute,
    getRemoteStream,
    getCallPeerIds,
    callStartTime: contextCallStartTime, // Get callStartTime from ChatContext for timer sync
  } = useChat();

  // Audio element refs: peerId → HTMLAudioElement
  const audioRefs = useRef({});
  const audioContainerRef = useRef(null);
  const [callDuration, setCallDuration] = useState(0);
  const callStartRef = useRef(null);
  const [muteFlash, setMuteFlash] = useState(false);
  const [connectionLost, setConnectionLost] = useState(false);
  const [reconnectingDots, setReconnectingDots] = useState(0);
  // Track callState transitions to avoid stale effect
  const prevCallStateRef = useRef(null);
  // Track if we've already started the timer
  const timerStartedRef = useRef(false);

  // Start duration timer - runs continuously once started
  // KEY FIX: Sync both timers by using the SAME server-provided start time
  // The server timestamp is captured in handleCallConnecting, stored in activeCall.callStartTime
  // This effect runs whenever callState or activeCall.callStartTime changes
  useEffect(() => {
    const isActive = callState === 'active' || callState === 'connecting';
    
    // Get server timestamp from activeCall.callStartTime (set by ChatContext)
    const serverStartTime = activeCall?.callStartTime;
    
    // Start timer if:
    // 1. We're in active/connecting state AND
    // 2. Timer not started yet AND
    // 3. We have a server timestamp OR we're in active state (fallback to Date.now())
    if (isActive && !timerStartedRef.current) {
      const startTime = serverStartTime || Date.now();
      callStartRef.current = startTime;
      timerStartedRef.current = true;
      console.log('[CallKit] Timer STARTED, callState:', callState, 'startTime:', startTime, 'serverStartTime:', serverStartTime);
    }
    
    // Reset when leaving active state
    if (!isActive) {
      callStartRef.current = null;
      timerStartedRef.current = false;
      setCallDuration(0);
    }
  }, [callState, activeCall?.callStartTime]);

  // Separate effect: update callDuration every second when call is active
  useEffect(() => {
    const isActive = callState === 'active' || callState === 'connecting';
    if (!isActive) return;
    
    // Initial duration calculation
    if (callStartRef.current) {
      setCallDuration(Math.floor((Date.now() - callStartRef.current) / 1000));
    }
    
    const interval = setInterval(() => {
      if (callStartRef.current) {
        const duration = Math.floor((Date.now() - callStartRef.current) / 1000);
        setCallDuration(duration);
      }
    }, 1000);
    
    return () => clearInterval(interval);
  }, [callState]);

  // Mute button flash animation on unmute
  useEffect(() => {
    if (!isMuted) {
      setMuteFlash(true);
      const t = setTimeout(() => setMuteFlash(false), 300);
      return () => clearTimeout(t);
    }
  }, [isMuted]);

  // Track connection failed state — auto-end only when ALL peers are failed
  useEffect(() => {
    const states = Object.values(participantStates);
    const failedPeers = states.filter(s => s === 'failed');
    const activePeers = states.filter(s => s === 'connected' || s === 'reconnecting');
    const allFailed = failedPeers.length > 0 && activePeers.length === 0;
    setConnectionLost(allFailed);
    if (allFailed) {
      const tid = setTimeout(() => endCurrentCall(), 5000);
      return () => clearTimeout(tid);
    }
  }, [participantStates, endCurrentCall]);

  // Animate reconnecting dots
  useEffect(() => {
    const hasReconnecting = Object.values(participantStates).some(s => s === 'reconnecting');
    if (!hasReconnecting) {
      setReconnectingDots(0);
      return;
    }
    const interval = setInterval(() => {
      setReconnectingDots(prev => (prev + 1) % 3);
    }, 500);
    return () => clearInterval(interval);
  }, [participantStates]);

  // Attach remote streams to audio elements imperatively (prevents duplicates, reuses existing)
  useEffect(() => {
    const container = audioContainerRef.current;
    if (!container) return;

    const peerIds = getCallPeerIds();

    // Create or reuse audio elements for peers
    peerIds.forEach(peerId => {
      const stream = getRemoteStream(peerId);
      if (!stream) return;
      const existingAudio = audioRefs.current[peerId];
      if (existingAudio) {
        // Reuse — update srcObject if stream changed
        if (existingAudio.srcObject !== stream) {
          existingAudio.srcObject = stream;
        }
        return;
      }
      // No element exists — create new
      const audio = document.createElement('audio');
      audio.srcObject = stream;
      audio.autoplay = true;
      audio.playsInline = true;
      audioRefs.current[peerId] = audio;
      container.appendChild(audio);
    });

    // Remove audio elements for peers no longer in call
    Object.keys(audioRefs.current).forEach(peerId => {
      const peerIdNum = parseInt(peerId);
      if (!peerIds.includes(peerIdNum) && !peerIds.includes(String(peerId))) {
        const el = audioRefs.current[peerId];
        if (el) {
          el.pause();
          el.srcObject = null;
          if (el.parentNode) el.parentNode.removeChild(el);
        }
        delete audioRefs.current[peerId];
      }
    });
  }, [remoteStreams, getRemoteStream, getCallPeerIds]);

  if (callState === 'idle') return null;

  const formatDuration = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const peerIds = getCallPeerIds();
  const participants = activeCall?.participants || [];

  const getParticipantInfo = (peerId) => {
    const p = participants.find(p => p.id === peerId);
    return p || { id: peerId, state: participantStates[peerId] };
  };

  const reconnectingLabel = () => {
    const dots = '.'.repeat(reconnectingDots + 1);
    return `${t('call.reconnecting')}${dots}`;
  };

  // ---- INCOMING CALL VIEW ----
  if (callState === 'incoming' && incomingCall) {
    return (
      <div className="callkit-overlay">
        <div className="callkit-modal callkit-incoming">
          <div className="callkit-caller-avatar">
            <Avatar
              user={{ id: incomingCall.initiatorId, display_name: incomingCall.initiatorName }}
              size="xlarge"
            />
          </div>
          <h2 className="callkit-caller-name">
            {incomingCall.initiatorName || t('call.unknownCaller')}
          </h2>
          <p className="callkit-status">
            {incomingCall.isGroup ? t('call.incomingGroupCall') : t('call.incomingCall')}
          </p>

          <div className="callkit-actions">
            <button
              className="callkit-btn callkit-btn-reject"
              onClick={() => rejectCall('busy')}
              title={t('call.reject')}
            >
              <IoCallSharp style={{ transform: 'rotate(135deg)' }} />
            </button>
            <button
              className="callkit-btn callkit-btn-accept"
              onClick={acceptCall}
              title={t('call.accept')}
            >
              <IoCall />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---- CALLING VIEW (initiator waiting) ----
  if (callState === 'calling') {
    return (
      <div className="callkit-overlay">
        <div className="callkit-modal callkit-calling">
          <div className="callkit-caller-avatar">
            <Avatar
              user={activeCall?.participants?.find(p => p.id !== activeCall?.initiatorId)}
              size="xlarge"
            />
            <span className="callkit-calling-badge">
              <IoCall className="callkit-pulse" />
            </span>
          </div>
          <h2 className="callkit-caller-name">
            {activeCall?.isGroup
              ? t('call.callingGroup')
              : activeCall?.participants?.find(p => p.id !== activeCall?.initiatorId)?.display_name
              || t('call.calling')}
          </h2>
          <p className="callkit-status">{t('call.callingStatus')}</p>

          <div className="callkit-actions">
            <button
              className="callkit-btn callkit-btn-end"
              onClick={endCurrentCall}
            >
              <IoCallSharp style={{ transform: 'rotate(135deg)' }} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---- CONNECTING VIEW ----
  if (callState === 'connecting') {
    return (
      <div className="callkit-overlay">
        <div className="callkit-modal callkit-connecting">
          <div className="callkit-caller-avatar">
            <Avatar
              user={activeCall?.participants?.find(p => p.id !== activeCall?.initiatorId)}
              size="xlarge"
            />
          </div>
          <h2 className="callkit-caller-name">
            {activeCall?.isGroup
              ? t('call.connectingGroup')
              : activeCall?.participants?.find(p => p.id !== activeCall?.initiatorId)?.display_name
              || t('call.connecting')}
          </h2>
          <p className="callkit-status">
            <span className="callkit-spinner" /> {t('call.connectingStatus')}
          </p>
          {/* End call button — always visible so user can cancel while connecting */}
          <div className="callkit-controls">
            <button
              className="callkit-ctrl-btn callkit-ctrl-btn--end"
              onClick={endCurrentCall}
              title={t('call.end')}
            >
              <IoCallSharp style={{ transform: 'rotate(135deg)' }} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---- ACTIVE CALL VIEW ----
  if (callState === 'active' || callState === 'connecting') {
    const displayParticipants = peerIds.map(peerId => {
      const p = getParticipantInfo(peerId);
      return {
        id: peerId,
        user: p,
        isMuted: remoteMuteStates[peerId] || false,
        connectionState: participantStates[peerId] || 'unknown',
        isSpeaking: speakingPeers[peerId] || false,
      };
    });

    const totalCount = displayParticipants.length + 1; // local + remote
    // For 1: 1col centered. For 2: 1col centered. For 3: 2col. For 4+: 2col
    const gridClass = totalCount === 1 || totalCount === 2
      ? 'callkit-grid--single'
      : `callkit-grid--${Math.min(totalCount, 4)}`;

    return (
      <div className="callkit-overlay callkit-active">
        {/* Hidden container for imperative audio elements */}
        <div ref={audioContainerRef} style={{ display: 'none' }} aria-hidden="true" />

        <div className="callkit-active-inner">
          {/* Header */}
          <div className="callkit-header">
            <span className="callkit-timer">{formatDuration(callDuration)}</span>
            <span className="callkit-participant-count">
              {totalCount} {t('call.participants')}
            </span>
          </div>

          {/* Connection lost banner */}
          {connectionLost && (
            <div className="callkit-connection-lost">
              {t('call.connectionLost')}
            </div>
          )}

          {/* Group size warning */}
          {activeCall?.isGroup && totalCount > 4 && (
            <div className="callkit-warning">
              {t('call.maxParticipantsWarning', { max: 4, current: totalCount })}
            </div>
          )}

          {/* Participant grid */}
          <div className={`callkit-grid ${gridClass}`}>
            {/* Local participant tile */}
            <div className={`callkit-tile ${isMuted ? 'callkit-tile--muted' : ''}`}>
              <div className="callkit-tile-avatar">
                <Avatar user={null} size="large" />
                <span className="callkit-tile-you-badge">{t('call.you')}</span>
              </div>
              {isMuted && (
                <div className="callkit-muted-indicator">
                  <IoMicOff />
                </div>
              )}
              <span className="callkit-tile-name">{t('call.you')}</span>
            </div>

            {/* Remote participant tiles */}
            {displayParticipants.map(participant => {
              const isFailed = participant.connectionState === 'failed';
              const isReconnecting = participant.connectionState === 'reconnecting';
              return (
                <div
                  key={participant.id}
                  className={`callkit-tile ${isReconnecting ? 'callkit-tile--reconnecting' : ''} ${isFailed ? 'callkit-tile--disconnected' : ''}`}
                >
                  <div className="callkit-tile-avatar">
                    <Avatar user={{ id: participant.id, display_name: participant.user?.display_name }} size="large" />
                    {participant.connectionState === 'connected' && (
                      <span className={`callkit-speaking-indicator ${participant.isSpeaking ? 'callkit-speaking-indicator--active' : ''}`} />
                    )}
                  </div>
                  {participant.isMuted && (
                    <div className="callkit-muted-indicator">
                      <IoMicOff />
                    </div>
                  )}
                  <span className="callkit-tile-name">
                    {participant.user?.display_name || `#${participant.id}`}
                  </span>
                  {isFailed && (
                    <span className="callkit-tile-state callkit-tile-state--error">
                      {t('call.connectionLost')}
                    </span>
                  )}
                  {isReconnecting && (
                    <span className="callkit-tile-state">
                      {reconnectingLabel()}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Controls */}
          <div className="callkit-controls">
            <button
              className={`callkit-ctrl-btn ${isMuted ? 'callkit-ctrl-btn--active' : ''} ${muteFlash ? 'callkit-ctrl-btn--flash' : ''}`}
              onClick={toggleMute}
              title={isMuted ? t('call.unmute') : t('call.mute')}
            >
              {isMuted ? <IoMicOff /> : <IoMic />}
            </button>

            <button
              className="callkit-ctrl-btn callkit-ctrl-btn--end"
              onClick={endCurrentCall}
              title={t('call.end')}
            >
              <IoCallSharp style={{ transform: 'rotate(135deg)' }} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---- ENDED VIEW ----
  if (callState === 'ended') {
    return (
      <div className="callkit-overlay callkit-overlay--ended">
        <div className="callkit-modal callkit-ended">
          <h2>{t('call.ended')}</h2>
          <p>{formatDuration(callDuration)}</p>
        </div>
      </div>
    );
  }

  return null;
};

export default CallKit;
